import type { AxiosResponse } from 'axios';

import { ENDPOINTS } from '@/constants/endpoints';
import type {
	BaseResponse,
	LoginSuccessResponse,
	RequestCheckOtpRequest,
	RequestCheckOtpSuccessResponse,
	RequestTokenRequest,
	RequestTokenSuccessResponse,
	RequestActivationEmailSuccessResponse,
	SignupProps,
	SignupRequest,
	SignupSuccessResponse,
} from '@/types';

import type { LoginFormData } from './login.model';
import { axiosInstance } from '@/services/axios.service';
import { v4 as uuidv4 } from 'uuid';
import { PhoneHelper } from '@/common/PhoneHelper';

//const axiosService = new AxiosService();

export interface ResendVerifyEmailRequest {
	email: string;
}

export const signup = async (
	payload: SignupProps,
): Promise<AxiosResponse<BaseResponse>> => {

	// Map OAuth provider name to AuthProviderType enum value
	const getAuthProviderType = (provider?: string | null): number => {
		if (provider === 'Google') return 2;
		if (provider === 'Facebook') return 3;
		return 1; // Internal
	};

	var result = await axiosInstance
		.post<
			BaseResponse,
			AxiosResponse<BaseResponse>,
			SignupRequest
		>(ENDPOINTS['/api/X4bUser/CreateUser'], {
			Uuid: uuidv4(),
			FirstName: payload.user.firstName,
			LastName: payload.user.lastName,
			//MobileNumberPrefix: payload.user.mobileNumberPrefix,
			Phone: PhoneHelper.preparePhoneNumber(payload.user.mobileNumber, payload.user.mobileNumberPrefix),
			Email: payload.user.email,
			Username: payload.user.username,
			Password: payload.user.password,
			ConfirmPassword: payload.user.password,
			RoleIds: [],//should be specified on server
			AuthProviderType: getAuthProviderType(payload.user.oauthProvider),
			ProviderKey: payload.user.oauthProviderKey || '',
			Company: {
				Uuid: payload.company.uuid,
				Name: payload.company.name,
				Address: payload.company.address,
				WorkEmail: payload.company.workEmail,
				CompanySize: payload.company.companySize,
				MobileNumber: payload.company.mobileNumber,
				NatureOfBusiness: payload.company.natureOfBusiness,
				NatureOfBusinessOther: payload.company.natureOfBusinessOther,
			},
		});
	if ((result.data as any)["TestLink"]) {
		console.log(`Activation test link: ${(result.data as any)["TestLink"]}`);
	}

	return result;
}

export const resendActivationEmail = async (email: string): Promise<
	AxiosResponse<RequestActivationEmailSuccessResponse>
> => {
	const res = await axiosInstance
		.post<
			RequestActivationEmailSuccessResponse,
			AxiosResponse<RequestActivationEmailSuccessResponse>
		>(ENDPOINTS['/api/X4bUser/ResendActivationEmail'], { email: email } as ResendVerifyEmailRequest, {
			skipGenericErrorHandler: true
		});
	const testLink = (res.data as any)?.Data["Link"];
	if (testLink) {
		console.log(`Resend activation email test link: ${testLink}`);
	}
	return res;
}

export const login = async (payload: LoginFormData): Promise<AxiosResponse<LoginSuccessResponse>> => {
	const formData = new FormData();
	formData.append('UserName', payload.email);
	formData.append('Password', payload.password);
	formData.append('AuthMethod', '1');

	return await axiosInstance.post<LoginSuccessResponse>(ENDPOINTS['/Login'], formData, {
		skipGenericErrorHandler: true
	});
};

export const checkOtp = async (
	req: RequestCheckOtpRequest,
): Promise<AxiosResponse<RequestCheckOtpSuccessResponse>> =>
	axiosInstance
		.post<
			RequestCheckOtpSuccessResponse,
			AxiosResponse<RequestCheckOtpSuccessResponse>,
			RequestCheckOtpRequest
		>(ENDPOINTS['/api/otp/CheckOtp'], req, {
			skipGenericErrorHandler: true
		});


// ... existing code ...

export interface ResendOtpRequest {
	ProcessId: string;
	BroadcastChannelType?: number;
}

export interface ResendOtpResponse {
	Success: boolean;
	ProcessId: string;
	ErrorMessage?: string;
}

export const resendOtp = async (
	req: ResendOtpRequest,
): Promise<AxiosResponse<ResendOtpResponse>> =>
	axiosInstance.post<ResendOtpResponse>(
		ENDPOINTS['/api/Otp/ResendOTP'],
		req,
		{ skipGenericErrorHandler: true },
	);

export const requestToken = async (processId: string): Promise<RequestTokenSuccessResponse> => {
	const formData = new FormData();
	formData.append('ProcessId', processId);

	const response = await axiosInstance.post<RequestTokenSuccessResponse>(ENDPOINTS['/Token'], formData);
	return response.data
};

export interface CheckEmailAvailabilityRequest {
	email: string;
}

export interface CheckEmailAvailabilityResponse {
	IsAvailable: boolean;
}

export const checkEmailAvailability = async (email: string): Promise<AxiosResponse<CheckEmailAvailabilityResponse>> => {
	return await axiosInstance.post<CheckEmailAvailabilityResponse>(
		ENDPOINTS['/api/X4bUser/CheckEmailAvailability'],
		{ email } as CheckEmailAvailabilityRequest,
		{ skipGenericErrorHandler: true }
	);
};
